/**
 * File: retainersService.ts
 * Path: apps/admin/src/shared/services/retainersService.ts
 * Description: Reads and writes carePlans/{id} (old records may still be in
 * retainers/{id}: reads look in carePlans first, writes go where the record
 * lives, new records go to carePlans): care plans added by hand and
 * the pause or cancellation of a signed retainer contract (then the id is the
 * contract's id). Firestore comes from a getter so the rules tests run these
 * exact writes against the emulator.
 */

import { CARE_PLANS_COLLECTION, db, FIRESTORE_COLLECTIONS, toRetainer, type Retainer } from "@idk/config";
import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc, type Firestore } from "firebase/firestore";

const RETAINERS = FIRESTORE_COLLECTIONS.retainers;
const CARE_PLANS = CARE_PLANS_COLLECTION;

/** What staff type for a retainer; the id is chosen by the repo. */
export type RetainerInput = Omit<Retainer, "id">;

const fields = (input: RetainerInput) => ({
  clientId: input.clientId,
  clientName: input.clientName,
  monthlyCents: input.monthlyCents,
  startDate: input.startDate,
  status: input.status,
  endDate: input.endDate,
  contractId: input.contractId,
  kind: input.kind,
  renewsOn: input.renewsOn,
  // Kept as saved unless the input says otherwise (the save merges, so other fields stay).
  ...(input.autoSend === undefined ? {} : { autoSend: input.autoSend === true }),
  updatedAt: serverTimestamp(),
});

export const createRetainersRepo = (getDb: () => Firestore) => {
  /** Where this plan lives: carePlans when it is there, retainers when only the old copy is, else carePlans. */
  const homeOf = async (id: string): Promise<string> => {
    if ((await getDoc(doc(getDb(), CARE_PLANS, id))).exists()) return CARE_PLANS;
    if ((await getDoc(doc(getDb(), RETAINERS, id))).exists()) return RETAINERS;
    return CARE_PLANS;
  };
  return {
    list: async (): Promise<Retainer[]> => {
      const [fresh, old] = await Promise.all([getDocs(collection(getDb(), CARE_PLANS)), getDocs(collection(getDb(), RETAINERS))]);
      const byId = new Map<string, Retainer>();
      for (const snap of [old, fresh]) {
        for (const d of snap.docs) {
          const retainer = toRetainer(d.id, d.data());
          if (retainer) byId.set(d.id, retainer);
        }
      }
      return [...byId.values()];
    },

    /** A care plan with no contract. Returns its id. */
    add: async (input: Omit<RetainerInput, "contractId" | "renewsOn"> & { renewsOn?: string | null }): Promise<string> => {
      const ref = doc(collection(getDb(), CARE_PLANS));
      await setDoc(ref, fields({ ...input, contractId: null, renewsOn: input.renewsOn ?? null }));
      return ref.id;
    },

    /** Saves a plan under its id: a contract's plan keeps the contract's id. */
    save: async (id: string, input: RetainerInput) =>
      setDoc(doc(getDb(), await homeOf(id), id), fields(input), { merge: true }),

    /** Whether the plan's monthly invoices go out by themselves (care plan billing, planBilling.ts). */
    setAutoSend: async (id: string, on: boolean) =>
      setDoc(doc(getDb(), await homeOf(id), id), { autoSend: on, updatedAt: serverTimestamp() }, { merge: true }),

    /** Removes the plan from both collections so the fallback cannot bring it back. */
    remove: async (id: string) => {
      await deleteDoc(doc(getDb(), CARE_PLANS, id));
      await deleteDoc(doc(getDb(), RETAINERS, id));
    },
  };
};

export const retainersRepo = createRetainersRepo(() => db);
